import React, { useEffect, useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage } from 'react-intl'

// Selectors
import { selectHomeLat, selectHomeLng } from 'src/store/selectors/home'
import { selectHomeValue } from 'src/store/selectors/home/metadata'
import { selectQualifiedEquityListings } from 'src/store/selectors/home/equity'
import {
  selectDisplayYlopoSpecificContent,
  selectListingsSourceIsHomebotListings,
} from 'src/store/selectors/listings'
import { selectCustomerId } from 'src/store/selectors/customerProfile'

// Actions
import { searchListingsForCarousel } from 'src/actions/listings'

// Hooks
import { useViewAllListingsUrl, useListingsAd } from 'src/hooks/listings'

// Utils
import history from 'src/history'

// constants
import { EquitySmartSearchCalculationClick } from 'src/constants/trackingEvents'
import { OVERLAY_HELPERS } from 'src/constants/navigation'
import {
  LISTINGS_EQUITY_SMART_SEARCH,
  listingsSearchCriteria,
} from 'src/constants/listings'

// Components
import { If, Text, Button } from 'src/Components'
import Format from 'src/Components/shared/Format'
import ListingsCarousel from 'src/Components/shared/ListingsCarousel'
import { selectShouldRenderEquitySmartSearchListings } from 'src/store/selectors/display'
import BotLogo from 'src/Components/shared/Logo/BotLogo'
import { EquitySmartSearchTable } from '../EquitySmartSearchTable'

// Styles
import styles from './EquitySmartSearchWithListings.module.scss'

export const idForScroll = 'EssTableSection'

const EquitySmartSearchWithListings = () => {
  const dispatch = useDispatch()
  const homeLat = useSelector(selectHomeLat)
  const homeLng = useSelector(selectHomeLng)
  const homeValue = useSelector(selectHomeValue)
  const qualifiedListings = useSelector(selectQualifiedEquityListings)
  const shouldRenderListings = useSelector(
    selectShouldRenderEquitySmartSearchListings
  )
  const [disableAutoScroll, setDisableAutoScroll] = useState(false)
  const isLoadingListings = qualifiedListings === null

  const showYlopoListings = useSelector(selectDisplayYlopoSpecificContent)
  const showHomebotListings = useSelector(selectListingsSourceIsHomebotListings)
  const customerId = useSelector(selectCustomerId)

  const minPriceRange =
    homeValue * listingsSearchCriteria.SIMILAR_HOME_MIN_PRICE_MULTIPLIER
  const maxPriceRange =
    homeValue * listingsSearchCriteria.SIMILAR_HOME_MAX_PRICE_MULTIPLIER

  const dataSearch = useMemo(
    () => ({
      circle: {
        lat: homeLat,
        lng: homeLng,
        radius:
          listingsSearchCriteria.EQUITY_SMART_SEARCH_SIMILAR_HOME_DISTANCE,
      },
      limit: listingsSearchCriteria.SIMILAR_HOME_LIMIT,
      minPrice: minPriceRange,
      maxPrice: maxPriceRange,
      orderBy: 'sourceCreationDate,desc',
      tag: LISTINGS_EQUITY_SMART_SEARCH,
    }),
    [homeLat, homeLng, minPriceRange, maxPriceRange]
  )

  useEffect(() => {
    if (showHomebotListings) {
      dispatch(searchListingsForCarousel(customerId, dataSearch))
    } else if (showYlopoListings) {
      if (window.YLOPO_WIDGETS.remount) {
        window.YLOPO_WIDGETS.remount({ force: true })
      }
    }
  }, [showYlopoListings, showHomebotListings, customerId, dispatch, dataSearch])

  useEffect(() => {
    if (!disableAutoScroll) {
      const element = document.getElementById(idForScroll)

      if (element && window.location.hash.includes(idForScroll)) {
        element.scrollIntoView()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingListings])

  useEffect(() => {
    // If our scrollTo element isn't ready to go in the first second after mount, don't hijack UX after someone's already doing whatever it is they did for that last second
    setTimeout(() => setDisableAutoScroll(true), 1000)
  }, [])

  const viewAllListingsUrl = useViewAllListingsUrl(dataSearch)

  const handleNavigateToCalculation = () => {
    history.push(OVERLAY_HELPERS.equitySmartSearch.buildPath())
  }

  const lastListingsObject = {
    id: 'lastSlide',
    url: viewAllListingsUrl,
  }

  const adSlide = useListingsAd()

  return (
    <>
      <If condition={showYlopoListings && !showHomebotListings}>
        <div
          style={{ display: 'none' }}
          className='YLOPO_resultsWidget'
          data-search={JSON.stringify(dataSearch)}
        />
      </If>
      <If condition={!isLoadingListings && !shouldRenderListings}>
        <EquitySmartSearchTable />
      </If>
      <If condition={!isLoadingListings && shouldRenderListings}>
        <div className={styles.headingContainer} id={idForScroll}>
          <Text
            element='h4'
            variant='neutralCool'
            variantCount='600'
            size='xs'
            weight='semibold'
            className={styles.title}
          >
            <FormattedMessage
              id='MarketSpotLight.equitySmartSearch'
              defaultMessage='Equity Smart Search'
            />
          </Text>
          <Text
            element='p'
            variant='neutralCool'
            variantCount='900'
            size='l'
            align='center'
            paragraph
            className={styles.cta}
          >
            <FormattedMessage
              id='MarketSpotLight.equitySmartSearch.pocket.move'
              defaultMessage='What if you sold your home for {homeValue}, pocketed some equity, and moved to a new home?'
              values={{
                homeValue: (
                  <Text element='span' variant='primary'>
                    <Format.Currency value={homeValue} abbreviate />
                  </Text>
                ),
              }}
            />
          </Text>
        </div>
        <div
          className={styles.equitySmartSearchWithListings}
          data-qa='equitySmartSearchWithListings'
        >
          <Button
            className={styles.calculateButton}
            track={EquitySmartSearchCalculationClick}
            onClick={handleNavigateToCalculation}
          >
            <div className={styles.nerdBot}>
              <BotLogo />
            </div>
            <Text
              className={styles.calculatedText}
              variant='secondary'
              variantCount='600'
            >
              <FormattedMessage
                data-qa='equitySmartSearchCalculationButton'
                id='MarketSpotLight.equitySmartSearch.howIsThisCalculated'
                defaultMessage='How is this calculated?'
              />
            </Text>
          </Button>
          <ListingsCarousel
            listings={qualifiedListings}
            lastSlide={lastListingsObject}
            adSlide={adSlide}
            includeFullAd
          />
        </div>
        <Button.ExternalListingsButton
          className={styles.listingsButton}
          listingsUrl={viewAllListingsUrl}
        >
          <FormattedMessage
            id='MarketSpotLight.equitySmartSearch.see.all.button'
            defaultMessage='See all homes'
          />
        </Button.ExternalListingsButton>
      </If>
    </>
  )
}

export default EquitySmartSearchWithListings
